import * as React from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import history from '../../../../../../shared/history';
import { ImageDropzone, ImageDropzoneLabels } from '../../shared/view/ImageDropzone';
import { CustomerDigitalStylingApi } from '../queries/CustomerDigitalStylingApi';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ImageModel, ImageUploadResponse } from '../../shared/types';
import {
    DigitalStylingThumbnails,
    DigitalStylingThumbnailsLabels
} from '../../shared/view/DigitalStylingThumbnails';
import { useImageDataUpdate } from '../../shared/state/useImageDataUpdate';
import { IMAGE_REFETCH_INTERVAL } from '../../shared/state/thumbnailUtils';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { CustomerOrSupportImage } from '../../adminView/uneditedImages/view/UploadUneditedImages';
import { CustomerImageSkeleton } from './CustomerImageSkeleton';

type DigitalStylingOrderCustomerEntryLabels = {
    uploadButton: string;
    tabPanelTitle: string;
    thumbnailTooltipDelete: string;
    confirmDeleteSingleTitle: string;
    confirmDeleteSingleAcceptButton: string;
    confirmDeleteSingleCancelButton: string;
    deleteMultipleButton: string;
    selectAllButton: string;
    deselectAllButton: string;
    deliverImagesButton: string;
    customerSendInButton: string;
    customerSendInWithoutMaterialButton: string;
} & ImageDropzoneLabels &
    DigitalStylingThumbnailsLabels;

type DigitalStylingOrderCustomerEntryProps = {
    labels: DigitalStylingOrderCustomerEntryLabels;
    orderId: number;
    customerImages: ImageModel[];
    onUploadComplete: () => void;
    onRetryUpload: () => void;
    setImageInProgress: (imageUploadResponse: ImageUploadResponse) => void;
    onUpload: (formData: FormData) => Promise<number[]>;
    onDeleteSingleImage: (imageId: number) => Promise<void>; // todo
    onDeleteMultipleImages: (imageIds: CustomerOrSupportImage[]) => void;
    onSendInMaterial: () => void;
    onSkipUpload: () => void;
};

type ExternalProps = {
    labels: DigitalStylingOrderCustomerEntryLabels;
    orderId: number;
};

function DigitalStylingOrderCustomerEntry({
    labels,
    orderId,
    customerImages,
    onUploadComplete,
    onUpload,
    setImageInProgress,
    onRetryUpload,
    onDeleteSingleImage,
    onDeleteMultipleImages,
    onSendInMaterial,
    onSkipUpload
}: DigitalStylingOrderCustomerEntryProps) {
    let formId = `customer-files-${orderId}`;
    let baseUrl = window.appConfig.apiUrl;
    let formAction = `${baseUrl}/views/digital-styling-upload/customer-file?orderId=${orderId}`;

    let {
        filesAdded,
        addFileToDropzone,
        removeFileFromDropzone,
        isDoneUploading,
        setIsDoneUploading
    } = useImageDataUpdate(customerImages, onUploadComplete);

    function handleUpload(formData: FormData): Promise<number[]> {
        setIsDoneUploading(false);
        return onUpload(formData);
    }

    return (
        <fieldset>
            {customerImages.map((imageData) => (
                <Box key={imageData.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', padding: '20px' }}>
                        <Stack
                            sx={{ justifyContent: 'flex-end', padding: '20px 0' }}
                            direction="row"
                            spacing={2}
                        >
                            {imageData.thumbnails.length === 0 && filesAdded.length === 0 ? (
                                <Button
                                    variant="contained"
                                    onClick={onSkipUpload}
                                    startIcon={<SendIcon />}
                                    color="secondary"
                                >
                                    {labels.customerSendInWithoutMaterialButton}
                                </Button>
                            ) : null}
                            {imageData.thumbnails.length > 0 || filesAdded.length > 0 ? (
                                <Button
                                    disabled={
                                        imageData.thumbnails.length === 0 ||
                                        (imageData.thumbnails.length > 0 && !isDoneUploading) ||
                                        (filesAdded.length === 0 && !isDoneUploading)
                                    }
                                    variant="contained"
                                    color="secondary"
                                    onClick={onSendInMaterial}
                                    startIcon={<SendIcon />}
                                >
                                    {labels.customerSendInButton}
                                </Button>
                            ) : null}
                        </Stack>
                        {imageData.thumbnails.length > 0 ? (
                            <DigitalStylingThumbnails
                                files={imageData.thumbnails}
                                filesInProgress={imageData.uploadsInProgress}
                                onDelete={onDeleteSingleImage}
                                onDeleteMultiple={onDeleteMultipleImages}
                                enableActions={isDoneUploading}
                                onClick={() => {}}
                                labels={labels}
                            />
                        ) : isDoneUploading ? null : (
                            <CustomerImageSkeleton />
                        )}
                        <ImageDropzone
                            labels={{
                                filesAdded: labels.filesAdded,
                                defaultMessage: labels.defaultMessage,
                                fallbackMessage: labels.fallbackMessage,
                                invalidFileType: labels.invalidFileType,
                                cancelUpload: labels.cancelUpload,
                                cancelUploadConfirmation: labels.cancelUploadConfirmation
                            }}
                            formAction={formAction}
                            formId={formId}
                            filesAdded={filesAdded}
                            addFile={addFileToDropzone}
                            removeFile={removeFileFromDropzone}
                            orderId={orderId}
                            onUpload={handleUpload}
                            setImageInProgress={setImageInProgress}
                            onRetryUpload={onRetryUpload}
                        />
                        <Button
                            variant="contained"
                            disabled={filesAdded.length < 1}
                            startIcon={<CloudUploadIcon />}
                            type="submit"
                            form={formId}
                            sx={{ alignSelf: 'flex-end' }}
                        >
                            {labels.uploadButton}
                        </Button>
                    </Box>
                </Box>
            ))}
        </fieldset>
    );
}

// TODO: order list doesn't show city!

function DigitalStylingOrderCustomerEntryDataLayer({
    labels,
    orderId,
    location
}: ExternalProps & RouteComponentProps) {
    let [refetchInterval, setRefetchInterval] = React.useState<number>(0);

    // TODO: Optimistic UI...

    let queryClient = useQueryClient();

    let queryKey = 'customer-files';
    let { data, isError, error } = useQuery(
        [queryKey],
        () => CustomerDigitalStylingApi.getCustomerFiles(orderId),
        {
            refetchInterval
        }
    );

    let deleteSingleImageMutation = useMutation(
        ({ imageId }: { imageId: number }) => CustomerDigitalStylingApi.deleteSingleImage(imageId),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deleteMultipleImagesMutation = useMutation(
        ({ imageIds }: { imageIds: number[] }) =>
            CustomerDigitalStylingApi.deleteMultipleImages(imageIds),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let sendInMaterialMutation = useMutation(() =>
        CustomerDigitalStylingApi.sendInMaterial(orderId)
    );
    
    let sendInWithoutMaterialMutation = useMutation(() =>
        CustomerDigitalStylingApi.sendInWithoutMaterial(orderId)
    );

    function handleDeleteSingleImage(imageId: number) {
        return deleteSingleImageMutation.mutateAsync({ imageId });
    }

    function handleDeleteMultipleImages(images: CustomerOrSupportImage[]) {
        return deleteMultipleImagesMutation.mutate({ imageIds: images.map(({ id }) => id) });
    }

    function handleUpload(formData: FormData): Promise<number[]> {
        // We don't need to save metadata for customer images
        let empty: number[] = [];
        return Promise.resolve(empty);
    }

    function setImageInProgress(imageUploadResponse: ImageUploadResponse) {
        setRefetchInterval(IMAGE_REFETCH_INTERVAL);
    }

    let searchParams = location.search ? new URLSearchParams(location.search) : null;
    let redirectTo = null;
    if (searchParams) {
        redirectTo = searchParams.get('redirectTo');
    }

    function handleSendInMaterial() {
        sendInMaterialMutation.mutateAsync().then(() => {
            if (redirectTo) {
                history.push(redirectTo);
            } else {
                history.push(`/orders/${orderId}/basic`);
            }
        });
    }

    function handleSkipUpload() {
        sendInWithoutMaterialMutation.mutateAsync().then(() => {
            if (redirectTo) {
                history.push(redirectTo);
            } else {
                history.push(`/orders/${orderId}/basic`);
            }
        });
    }

    if (isError) {
        console.error('Error fetching customer files', error);
    }

    return (
        <DigitalStylingOrderCustomerEntry
            labels={labels}
            orderId={orderId}
            customerImages={data ? data : []}
            onUpload={handleUpload}
            setImageInProgress={setImageInProgress}
            onUploadComplete={() => {
                setRefetchInterval(0);
            }}
            onRetryUpload={() => {
                if (refetchInterval === 0) {
                    setRefetchInterval(IMAGE_REFETCH_INTERVAL);
                }
            }}
            onDeleteSingleImage={handleDeleteSingleImage}
            onDeleteMultipleImages={handleDeleteMultipleImages}
            onSendInMaterial={handleSendInMaterial}
            onSkipUpload={handleSkipUpload}
        />
    );
}

export default withRouter(DigitalStylingOrderCustomerEntryDataLayer);
